import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Zap } from 'lucide-react';
import { usePersistedBoolean } from '../../hooks/usePersistedBoolean';
import type { OrganizationWithPeople, BlastFundraiser } from '../../types';

const SECTION_KEY = 'crm:orgPage:blastFundraisersSection:expanded';

interface BlastFundraisersProps {
  organization: OrganizationWithPeople;
}

function schoolYearOf(iso: string): { start: number; label: string } {
  const d = new Date(iso);
  const month = d.getUTCMonth();
  const startYear = month >= 6 ? d.getUTCFullYear() : d.getUTCFullYear() - 1;
  const endTwoDigit = String((startYear + 1) % 100).padStart(2, '0');
  return { start: startYear, label: `${startYear}-${endTwoDigit}` };
}

function currentSyStart(now: Date = new Date()): number {
  const month = now.getUTCMonth();
  return month >= 6 ? now.getUTCFullYear() : now.getUTCFullYear() - 1;
}

function formatDollars(cents: string): string {
  const dollars = Number(BigInt(cents)) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(dollars);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC',
  });
}

interface TeamRef {
  label: string;
  sportId: string;
}

function buildTeamRefMap(organization: OrganizationWithPeople): Map<string, TeamRef> {
  const map = new Map<string, TeamRef>();
  for (const sport of organization.sports) {
    for (const team of sport.teams) {
      map.set(team.id, { label: `${sport.name} ${team.level}`, sportId: sport.id });
    }
  }
  return map;
}

interface YearBucket {
  start: number;
  label: string;
  fundraisers: BlastFundraiser[];
  totalCents: bigint;
}

function bucketBySchoolYear(fundraisers: BlastFundraiser[]): YearBucket[] {
  const buckets = new Map<number, YearBucket>();
  for (const f of fundraisers) {
    if (!f.launchedAt) continue;
    const sy = schoolYearOf(f.launchedAt);
    let b = buckets.get(sy.start);
    if (!b) {
      b = { start: sy.start, label: sy.label, fundraisers: [], totalCents: 0n };
      buckets.set(sy.start, b);
    }
    b.fundraisers.push(f);
    if (f.totalRaisedCents) b.totalCents += BigInt(f.totalRaisedCents);
  }
  return Array.from(buckets.values()).sort((a, b) => b.start - a.start);
}

export const BlastFundraisers = ({ organization }: BlastFundraisersProps) => {
  const [isExpanded, setIsExpanded] = usePersistedBoolean(SECTION_KEY, true);

  const buckets = useMemo(
    () => bucketBySchoolYear(organization.blastFundraisers),
    [organization.blastFundraisers],
  );
  const teamRefMap = useMemo(() => buildTeamRefMap(organization), [organization]);
  const currentSy = currentSyStart();

  if (organization.lifetimeFundraiserCount === 0) return null;

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
          )}
          <Zap className="h-5 w-5 text-blast-600 dark:text-blast-400 flex-shrink-0" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            Blast Fundraisers
          </h2>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
          {organization.lifetimeFundraiserCount} {organization.lifetimeFundraiserCount === 1 ? 'fundraiser' : 'fundraisers'}
        </span>
      </button>
      {isExpanded && (
        <div className="p-5 space-y-3">
          {buckets.map((bucket) => (
            <YearBucketBlock
              key={bucket.start}
              bucket={bucket}
              teamRefMap={teamRefMap}
              defaultExpanded={bucket.start === currentSy}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function YearBucketBlock({ bucket, teamRefMap, defaultExpanded }: { bucket: YearBucket; teamRefMap: Map<string, TeamRef>; defaultExpanded: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const totalDollars = formatDollars(bucket.totalCents.toString());
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />}
          <span className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{bucket.label} school year</span>
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-400 ml-6 sm:ml-0">
          {bucket.fundraisers.length} fundraisers · {totalDollars}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
          {bucket.fundraisers.map((f) => (
            <FundraiserRow key={f.id} fundraiser={f} teamRef={f.teamId ? teamRefMap.get(f.teamId) : undefined} />
          ))}
        </div>
      )}
    </div>
  );
}

function FundraiserRow({ fundraiser, teamRef }: { fundraiser: BlastFundraiser; teamRef: TeamRef | undefined }) {
  const dollars = fundraiser.totalRaisedCents ? formatDollars(fundraiser.totalRaisedCents) : '—';
  const date = fundraiser.launchedAt ? formatDate(fundraiser.launchedAt) : '—';
  const pct = fundraiser.participationPct ? Math.round(Number(fundraiser.participationPct)) : null;
  const donations = fundraiser.donationCount ?? null;
  const donors = fundraiser.donorCount ?? null;
  const avg = fundraiser.avgGiftCents ? formatDollars(fundraiser.avgGiftCents) : null;
  const recurring = fundraiser.recurringRaisedCents && fundraiser.recurringRaisedCents !== '0' ? formatDollars(fundraiser.recurringRaisedCents) : null;
  const oneTime = fundraiser.oneTimeRaisedCents && fundraiser.oneTimeRaisedCents !== '0' ? formatDollars(fundraiser.oneTimeRaisedCents) : null;
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2 px-4 py-2 text-sm">
      <div className="flex flex-col min-w-0">
        <div className="text-gray-700 dark:text-gray-300">
          {teamRef ? (
            <a
              href={`#sport-${teamRef.sportId}`}
              className="text-blast-600 dark:text-blast-400 hover:text-blast-700 dark:hover:text-blast-300 hover:underline transition-colors"
            >
              {teamRef.label}
            </a>
          ) : (
            <span title="Team not linked in CRM">Team #{fundraiser.blastTeamId}</span>
          )}
        </div>
        {fundraiser.reps.length > 0 && <FundraiserReps reps={fundraiser.reps} />}
        {donations !== null && (
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {`${donations} donations${donors !== null ? ` · ${donors} donors` : ''}${avg ? ` · ${avg} avg` : ''}`}
            {recurring && (
              <span className="block sm:inline sm:ml-2 text-gray-400 dark:text-gray-500">
                {`Recurring ${recurring}${oneTime ? ` · One-time ${oneTime}` : ''}`}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-gray-600 dark:text-gray-400 sm:flex-shrink-0 sm:gap-x-4">
        {fundraiser.athleteCount !== null && (
          <span>{fundraiser.athleteCount} athletes{pct !== null ? ` · ${pct}%` : ''}</span>
        )}
        <span className="font-medium text-gray-900 dark:text-gray-100">{dollars}</span>
        <span>{date}</span>
      </div>
    </div>
  );
}

function FundraiserReps({ reps }: { reps: BlastFundraiser['reps'] }) {
  const ordered = [...reps].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    const aBegin = a.activeBegin ?? '';
    const bBegin = b.activeBegin ?? '';
    return aBegin.localeCompare(bBegin);
  });
  return (
    <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
      {ordered.map((rep) => {
        const displayName = rep.salesRep?.displayName ?? rep.blastUserDisplayName ?? `Unknown (${rep.blastUserId})`;
        const isRotatedOff = rep.activeEnd !== null;
        return (
          <span
            key={rep.id}
            data-testid="fundraiser-rep-name"
            title={isRotatedOff ? 'Rotated off this fundraiser' : undefined}
            className={
              'inline-flex items-center gap-1 ' +
              (isRotatedOff ? 'italic text-gray-400 dark:text-gray-500' : '')
            }
          >
            {rep.isPrimary && (
              <span className="text-[10px] uppercase tracking-wide font-semibold text-blast-600 dark:text-blast-400">
                Primary
              </span>
            )}
            {displayName}
          </span>
        );
      })}
    </div>
  );
}
